/**
 * Scheduled Agent Jobs — types matching the backend API contract.
 *
 * Admins create recurring jobs from the UI. Each job has a name, a cadence,
 * and a natural-language prompt. A Celery tick fires the agent on schedule
 * and records the output in scheduled_job_runs.
 *
 * Source of truth for this contract:
 *   good-outcomes-api/docs/plans/2026-04-21-scheduled-agent-jobs-design.md
 */

export type ScheduleCadence =
  | 'every_5m'
  | 'every_10m'
  | 'every_30m'
  | 'every_60m'
  | 'daily';

export type JobStatus = 'active' | 'paused';

export type RunStatus =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'cancelled';

export interface ScheduledJobRun {
  id: string;
  job_id: string;
  status: RunStatus;
  celery_task_id: string | null;
  started_at: string | null;
  finished_at: string | null;
  output: string | null;
  error: string | null;
  created_at: string;
}

export interface ScheduledJob {
  id: string;
  organization_id: string;
  name: string;
  prompt: string;
  cadence: ScheduleCadence;
  daily_time_utc: string | null; // HH:MM format when cadence = 'daily'
  status: JobStatus;
  last_run_id: string | null;
  last_run_at: string | null;
  next_run_at: string | null;
  created_by_id: string;
  created_at: string;
  updated_at: string;
  /** Joined latest run summary on the list endpoint. */
  latest_run?: ScheduledJobRun | null;
}

export interface ScheduledJobDetail extends ScheduledJob {
  recent_runs: ScheduledJobRun[];
}

export interface CreateScheduledJobPayload {
  name: string;
  cadence: ScheduleCadence;
  prompt: string;
  daily_time_utc?: string;
}

export type UpdateScheduledJobPayload = Partial<CreateScheduledJobPayload>;

export interface ListScheduledJobsResponse {
  items: ScheduledJob[];
  total: number;
  limit: number;
  offset: number;
}

export interface ListScheduledJobRunsResponse {
  items: ScheduledJobRun[];
  total: number;
  limit: number;
  offset: number;
}

export const CADENCE_LABELS: Record<ScheduleCadence, string> = {
  every_5m: 'Every 5 minutes',
  every_10m: 'Every 10 minutes',
  every_30m: 'Every 30 minutes',
  every_60m: 'Every hour',
  daily: 'Daily',
};

export const formatCadence = (
  cadence: ScheduleCadence,
  dailyTimeUtc?: string | null,
): string => {
  if (cadence === 'daily') {
    const time = dailyTimeUtc && /^\d{2}:\d{2}/.test(dailyTimeUtc)
      ? dailyTimeUtc.slice(0, 5)
      : '09:00';
    return `Daily at ${time} UTC`;
  }
  return CADENCE_LABELS[cadence];
};
